<script lang="ts">
  import {
    bannerShown,
    watchBanner,
    type BannerVisibility,
  } from 'shared/javascripts/video_banners/visibility'
  import type { BannerData } from 'shared/javascripts/video_banners/types'
  import { trackClick } from 'frontend/shared/javascripts/tracking'

  const {
    banner,
    container,
    onPause,
    onQuizRequested,
    onLearningExercisesRequested,
  }: {
    banner: BannerData
    container?: HTMLElement
    onPause: () => void
    onQuizRequested?: () => void
    onLearningExercisesRequested?: (event: MouseEvent, url: string) => void
  } = $props()

  let visibility = $state<BannerVisibility>({
    inTimeWindow: false,
    adRunning: false,
    closed: false,
  })
  let watched: ReturnType<typeof watchBanner> | undefined

  $effect(() => {
    if (!container) {
      return
    }

    watched = watchBanner(container, banner, (next) => {
      visibility = next
    })

    return () => {
      watched?.destroy()
      watched = undefined
    }
  })

  const classes = $derived(
    [
      'video-banner',
      banner.kind !== 'link' && '-full-width',
      bannerShown(visibility) && '-visible',
      visibility.adRunning && '-ad-running',
      visibility.closed && '-closed',
    ]
      .filter(Boolean)
      .join(' '),
  )

  function close(event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    watched?.close()
  }

  // The close hook of a link banner sits inside the <a>, and the Unpoly app follows links on `mousedown`
  // — so the page would be gone before the click ever reached `close`. Keeping the mousedown to ourselves
  // is what stops that; the Inertia app has nothing listening and does not care.
  function keepEvent(event: Event): void {
    event.stopPropagation()
  }

  function pausePlayback(): void {
    onPause()
  }

  function startQuiz(): void {
    pausePlayback()
    watched?.close()
    onQuizRequested?.()
  }

  function startLearningExercises(event: MouseEvent, url: string): void {
    pausePlayback()
    onLearningExercisesRequested?.(event, url)
  }
</script>

{#snippet closeButton()}
  <span
    class="video-banner--close"
    role="button"
    tabindex="0"
    aria-label="Banner schließen"
    onclick={close}
    onmousedown={keepEvent}
    onkeydown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') close(event)
    }}
  ></span>
{/snippet}

{#if banner.kind === 'link'}
  <a
    href={banner.url}
    class={classes}
    target={banner.external ? '_blank' : null}
    rel={banner.external ? 'noopener' : null}
    onclick={pausePlayback}
    {@attach trackClick({ id: 'video-banner' })}
  >
    <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-built fragment -->
    <div class="video-banner--text">{@html banner.text_html}</div>
    {@render closeButton()}
  </a>
{:else}
  <div class={classes}>
    <div class="cta">
      <h4 class="cta--title">{banner.title}</h4>
      {#if banner.kind === 'learning_exercises'}
        {@const url = banner.url}
        <!-- tracking: video_page.exercises_video_banner -->
        <a
          href={url}
          class="cta--button"
          up-follow="false"
          onclick={(event) => startLearningExercises(event, url)}
          {@attach trackClick({ id: 'video-learning-exercises-banner' })}
        >
          {banner.label}
        </a>
      {:else}
        <button type="button" class="cta--button" onclick={startQuiz}>{banner.label}</button>
      {/if}
    </div>
    {@render closeButton()}
  </div>
{/if}

<style>
  .video-banner {
    color: var(--color-text);
    text-decoration: none;
  }

  .video-banner--close {
    cursor: pointer;
  }

  .cta {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: var(--space-2);
  }

  .cta--title {
    margin: 0 0 var(--space-2);
    font-size: var(--font-size-lg);
    text-align: center;
  }

  .cta--button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-2) var(--space-4);
    border: 1px solid var(--color-primary);
    border-radius: var(--border-radius-pill);
    background-color: var(--color-primary);
    color: var(--color-white);
    font-size: var(--font-size-base);
    font-weight: 700;
    line-height: 1.55;
    text-align: center;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
  }

  .cta--button:hover {
    background-color: #0aa7ef;
    border-color: #0a9fe2;
    color: var(--color-white);
  }

  .cta--button:active {
    background-color: #0a9fe2;
    border-color: #0996d6;
  }

  @media (--lg-down) {
    .cta--title {
      font-size: var(--font-size-base);
    }

    .cta--button {
      font-size: var(--font-size-sm);
    }
  }
</style>
